import { ROLE_DESCRIPTIONS } from '@hoopoe/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { UserCheck } from 'lucide-react';
import { useState } from 'react';
import { NotificationSettingsDialog } from '../components/auth/email-notifications';
import { useReauth } from '../components/auth/reauth';
import { EmptyState } from '../components/guidance/empty-state';
import { FieldHelp } from '../components/guidance/field-help';
import { PageHeader } from '../components/layout/app-shell';
import { Button } from '../components/ui/button';
import { ApiError, api } from '../lib/api';
import { useMe } from '../lib/session';

export const Route = createFileRoute('/admin/users')({ component: UsersPage });

type Role = 'admin' | 'editor' | 'reviewer' | 'viewer';
const ROLES: Role[] = ['viewer', 'editor', 'reviewer', 'admin'];
const ROLE_HELP = ROLES.map(r => `${r[0]?.toUpperCase()}${r.slice(1)}: ${ROLE_DESCRIPTIONS[r]}`).join(' ');
interface AccessRequest {
  id: string;
  email: string;
  name: string | null;
  reason: string | null;
  status: 'pending' | 'approved' | 'rejected';
  createdAt: number;
}
interface User {
  id: string;
  email: string;
  name: string | null;
  role: Role;
  status: 'pending' | 'active' | 'suspended';
  totpEnrolledAt: number | null;
  passwordSetAt: number | null;
  lastLoginAt: number | null;
  /** Receives system alerts (D472). */
  technicalContact: boolean;
  /** How their notifications are set (D472). */
  notifications: 'default' | 'off' | 'custom';
}
const NOTIFICATIONS_LABEL = { default: 'Defaults', off: 'All off', custom: 'Own choices' } as const;
const date = (ms: number | null) => (ms ? new Date(ms).toISOString().slice(0, 10) : '—');

function UsersPage() {
  const qc = useQueryClient();
  const me = useMe().data;
  const [error, setError] = useState<string | null>(null);
  const requests = useQuery({
    queryKey: ['admin', 'requests'],
    queryFn: () => api<{ requests: AccessRequest[] }>('/admin/access-requests'),
  });
  const users = useQuery({ queryKey: ['admin', 'users'], queryFn: () => api<{ users: User[] }>('/admin/users') });
  const { withReauth } = useReauth();
  const act = useMutation({
    mutationFn: (fn: () => Promise<unknown>) => withReauth(fn),
    onSuccess: () => {
      setError(null);
      qc.invalidateQueries({ queryKey: ['admin'] });
    },
    onError: e => {
      if (e instanceof ApiError && e.code === 'reauth_cancelled') return;
      setError(e instanceof ApiError ? e.message : 'Something went wrong.');
    },
  });
  const pending = requests.data?.requests.filter(r => r.status === 'pending') ?? [];
  const activeAdmins = users.data?.users.filter(u => u.role === 'admin' && u.status === 'active') ?? [];
  const contacts = activeAdmins.filter(u => u.technicalContact);
  const [roleFor, setRoleFor] = useState<Record<string, Role>>({});
  const [notificationsOf, setNotificationsOf] = useState<User | null>(null);

  return (
    <div className="space-y-8">
      <PageHeader title="Users & access" description="Hoopoe is invite-only: people request access, an admin approves them with a role." />
      {error && (
        <p role="alert" className="text-sm text-danger-foreground">
          {error}
        </p>
      )}
      <section aria-labelledby="req-title" className="space-y-3">
        <h2 id="req-title" className="font-semibold">
          Access requests <span className="text-muted-foreground tabular">({pending.length})</span>
        </h2>
        {pending.length === 0 ? (
          <EmptyState icon={UserCheck} title="No pending requests">
            New requests appear here and in your notifications.
          </EmptyState>
        ) : (
          <ul className="divide-y rounded-lg border bg-surface">
            {pending.map(r => (
              <li key={r.id} className="flex flex-wrap items-center gap-3 p-3 text-sm" aria-label={`Request from ${r.email}`}>
                <div className="min-w-0 flex-1">
                  <p className="font-medium">
                    <bdi dir="auto">{r.name}</bdi> · {r.email}
                  </p>
                  {r.reason && (
                    <p className="text-muted-foreground" dir="auto">
                      {r.reason}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground tabular">Requested {date(r.createdAt)}</p>
                </div>
                <label className="flex items-center gap-1">
                  <span className="sr-only">Role for {r.email}</span>
                  <select
                    value={roleFor[r.id] ?? 'viewer'}
                    onChange={e => setRoleFor({ ...roleFor, [r.id]: e.target.value as Role })}
                    className="h-8 rounded-md border border-input bg-surface px-2"
                  >
                    {ROLES.map(x => (
                      <option key={x}>{x}</option>
                    ))}
                  </select>
                  <FieldHelp label="Roles" help={ROLE_HELP} />
                </label>
                <Button
                  size="sm"
                  onClick={() =>
                    act.mutate(() => api(`/admin/access-requests/${r.id}/approve`, { body: { role: roleFor[r.id] ?? 'viewer' } }))
                  }
                >
                  Approve
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => act.mutate(() => api(`/admin/access-requests/${r.id}/reject`, { body: {} }))}
                >
                  Reject
                </Button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section aria-labelledby="users-title" className="space-y-3">
        <h2 id="users-title" className="font-semibold">
          Users
        </h2>
        {users.data && (
          <p className="flex items-center gap-1 text-sm text-muted-foreground" data-testid="technical-contacts">
            {contacts.length
              ? `System alerts go to ${contacts.length === 1 ? 'one technical contact' : `${contacts.length} technical contacts`}: ${contacts.map(u => u.name ?? u.email).join(', ')}.`
              : 'No technical contact is chosen, so system alerts go to every admin.'}
            <FieldHelp
              label="System alerts"
              help="System alerts say when part of Hoopoe has stopped working for a while, a job is stuck or the AI budget runs low. They go by email to the admins ticked under System alerts. If nobody is ticked, every admin gets them. This only changes email, not what anyone can do."
              learnMore="email-notifications"
            />
          </p>
        )}
        <div className="relative overflow-x-auto rounded-lg border bg-surface">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-start text-muted-foreground">
                <th className="p-2 text-start font-medium">User</th>
                <th className="p-2 text-start font-medium">Role</th>
                <th className="p-2 text-start font-medium">Status</th>
                <th className="p-2 text-start font-medium">Password</th>
                <th className="p-2 text-start font-medium">Authenticator</th>
                <th className="p-2 text-start font-medium">Last sign-in</th>
                <th className="p-2 text-start font-medium">System alerts</th>
                <th className="p-2 text-start font-medium">Notifications</th>
                <th className="p-2 text-start font-medium">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {users.data?.users.map(u => (
                <tr key={u.id} className="border-b last:border-0" aria-label={u.email}>
                  <td className="p-2">
                    <span className="font-medium">
                      <bdi dir="auto">{u.name ?? '—'}</bdi>
                    </span>
                    <span className="block text-xs text-muted-foreground">{u.email}</span>
                  </td>
                  <td className="p-2">
                    <label>
                      <span className="sr-only">Role of {u.email}</span>
                      <select
                        value={u.role}
                        onChange={e => {
                          // Capture now: a retry after re-auth runs after React has reset this controlled select.
                          const role = e.target.value;
                          act.mutate(() => api(`/admin/users/${u.id}`, { method: 'PATCH', body: { role } }));
                        }}
                        className="h-8 rounded-md border border-input bg-surface px-2"
                      >
                        {ROLES.map(x => (
                          <option key={x}>{x}</option>
                        ))}
                      </select>
                    </label>
                  </td>
                  <td className="p-2">{u.status}</td>
                  <td className="p-2 tabular">{u.passwordSetAt ? `Set ${date(u.passwordSetAt)}` : 'Not set'}</td>
                  <td className="p-2 tabular">{u.totpEnrolledAt ? `Set up ${date(u.totpEnrolledAt)}` : 'Not set up'}</td>
                  <td className="p-2 tabular">{date(u.lastLoginAt)}</td>
                  <td className="p-2">
                    {u.role === 'admin' ? (
                      <label className="flex items-center gap-1.5 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={u.technicalContact}
                          onChange={e => {
                            const technicalContact = e.target.checked;
                            act.mutate(() => api(`/admin/users/${u.id}`, { method: 'PATCH', body: { technicalContact } }));
                          }}
                        />
                        Receives <span className="sr-only">system alerts: {u.email}</span>
                      </label>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="p-2 whitespace-nowrap" data-testid="notifications-summary">
                    {u.status === 'active' ? (
                      <span className="flex items-center gap-1.5">
                        {NOTIFICATIONS_LABEL[u.notifications]}
                        <Button size="sm" variant="ghost" context={`for ${u.email}`} onClick={() => setNotificationsOf(u)}>
                          Change
                        </Button>
                      </span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="p-2">
                    <div className="flex justify-end gap-1">
                      {u.status === 'active' && (
                        <Button
                          size="sm"
                          variant="outline"
                          context={`to ${u.email}`}
                          onClick={() => {
                            if (
                              confirm(
                                u.passwordSetAt
                                  ? `Email ${u.email} a link to choose a new password? The link works for 1 hour; their current password keeps working until they use it.`
                                  : `Email ${u.email} a link to set their password? The link works for 7 days.`,
                              )
                            )
                              act.mutate(() => api(`/admin/users/${u.id}/password-link`, { body: {} }));
                          }}
                        >
                          Send password link
                        </Button>
                      )}
                      {u.totpEnrolledAt && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            if (
                              confirm(
                                `Reset the authenticator for ${u.email}? They will be signed out and set up a new one at next sign-in.`,
                              )
                            )
                              act.mutate(() => api(`/admin/users/${u.id}/reset-totp`, { body: {} }));
                          }}
                        >
                          Reset authenticator
                        </Button>
                      )}
                      {u.id !== me?.id &&
                        (u.status === 'suspended' ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => act.mutate(() => api(`/admin/users/${u.id}`, { method: 'PATCH', body: { status: 'active' } }))}
                          >
                            Reactivate
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() =>
                              act.mutate(() => api(`/admin/users/${u.id}`, { method: 'PATCH', body: { status: 'suspended' } }))
                            }
                          >
                            Suspend
                          </Button>
                        ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      <NotificationSettingsDialog
        open={!!notificationsOf}
        onOpenChange={o => !o && setNotificationsOf(null)}
        user={notificationsOf ? { id: notificationsOf.id, email: notificationsOf.email } : undefined}
      />
    </div>
  );
}
